import { Injectable } from '@angular/core';
import {
    VacationRequestsControllerService,
    VacationRequestsFilter,
    VacationRequestsReportControllerService,
    VacationRequestsControllerSetManualPendingDaysRequest,
} from '@api-swagger-client/index';
import { ReportFilter } from '@app/users/reports/users-reports.service';

import { Observable, catchError, throwError, of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Alcance del listado de solicitudes de vacaciones.
 * - mine: propias + aquellas donde el usuario es aprobador.
 * - pending: solo las pendientes de su autorizacion.
 * - all: todas las que permite su rol (capital humano / super admin).
 */
export type VacationRequestScope = 'mine' | 'pending' | 'all';

export interface CreateVacationRequestDto {
    startDate: Date;
    endDate: Date;
    employeeComments?: string;
    manualApproverId?: number;
    manualApproverReason?: string;
    // Adelanto de días del próximo período cuando el saldo actual no alcanza
    isAdvance?: boolean;
    advanceReason?: string;
}

export interface UpdateVacationRequestDto {
    startDate?: Date;
    endDate?: Date;
    employeeComments?: string;
    manualApproverId?: number;
    manualApproverReason?: string;
    isAdvance?: boolean;
    advanceReason?: string;
}

export interface ApproveRejectDto {
    approverComments?: string;
}

export interface VacationRequest {
    id?: string;
    status?: string;
    code?: string;
    startDate?: Date | string;
    endDate?: Date | string;
    totalDays?: number;
    crossesAnniversary?: boolean;
    daysInCurrentPeriod?: number;
    daysInNextPeriod?: number;
    user?: {
        firstName: string;
        middleName?: string;
        lastName: string;
    };
}

@Injectable({
    providedIn: 'root',
})
export class VacationsService {
    constructor(
        private vacationRequestControllerService: VacationRequestsControllerService,
        private vacationRequestsReportControllerService: VacationRequestsReportControllerService,
    ) {}

    // ==========================================
    // SOLICITUDES DE VACACIONES - CRUD
    // ==========================================

    /**
     * Crear una nueva solicitud de vacaciones (DRAFT)
     */
    createVacationRequest(data): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerCreate(data)
            .pipe(
                catchError((error) => {
                    console.error('Error creating vacation request:', error);
                    return throwError(error);
                }),
            );
    }

    /**
     * Actualizar una solicitud en estado DRAFT
     */
    updateVacationRequest(id: string, data): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerUpdateById(id, data)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error updating vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Obtener una solicitud por ID
     */
    getVacationRequestById(id: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerFindById(id)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error fetching vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Obtener una solicitud por ID desde el link público (sin autenticación)
     */
    findByIdFromLink(id: string, token: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerFindByIdFromLink(id, token)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error fetching vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Eliminar una solicitud (solo en estado DRAFT)
     */
    deleteVacationRequest(id: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerDeleteById(id)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error deleting vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Obtener todas las solicitudes del usuario actual
     */
    getMyVacationRequests(filterString?: any): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerFindMyRequests()
            .pipe(
                catchError((error) => {
                    console.error(
                        'Error fetching my vacation requests:',
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    // ==========================================
    // FLUJO DE APROBACIÓN
    // ==========================================

    /**
     * Enviar solicitud para aprobación
     */
    submitVacationRequest(id: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerSubmitRequest(id)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error submitting vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Checa si se puede aprobar/rechazar la solicitud (si el usuario es aprobador)
     */
    canProcess(id: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerCanApproveRequest(id)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error checking if user can process vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Checa si se puede aprobar/rechazar la solicitud (si el usuario es aprobador) del link publico
     */
    canProcessFromLink(id: string, token: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerCanApproveRequestFromLink(id, token)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error checking if user can process vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Aprobar solicitud
     */
    approveVacationRequest(id: string, data): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerApproveRequest(id, data)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error approving vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Rechazar solicitud
     */
    rejectVacationRequest(id: string, data): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerRejectRequest(id, data)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error rejecting vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Aprobar solicitud desde el link público (sin autenticación)
     */
    approveVacationRequestFromLink(
        id: string,
        token: string,
        data,
    ): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerApproveRequestFromLink(id, token, data)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error approving vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Rechazar solicitud desde el link público (sin autenticación)
     */
    rejectVacationRequestFromLink(
        id: string,
        token: string,
        data,
    ): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerRejectRequestFromLink(id, token, data)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error rejecting vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }
    /**
     * Cancelar solicitud
     */
    cancelVacationRequest(id: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerCancelRequest(id)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error cancelling vacation request ${id}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    // ==========================================
    // BALANCE DE VACACIONES
    // ==========================================

    /**
     * Obtener balance de vacaciones del usuario actual
     */
    getMyVacationBalance(): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerGetMyBalance()
            .pipe(
                catchError((error) => {
                    console.error('Error fetching my vacation balance:', error);
                    return throwError(error);
                }),
            );
    }

    /**
     * Proyección del próximo período de vacaciones del usuario actual.
     * Devuelve días totales, reservados y disponibles para el siguiente período.
     * NOTA: requiere regenerar el cliente swagger después de agregar el endpoint en el backend.
     */
    getNextPeriodProjection(): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerGetNextPeriodProjection()
            .pipe(
                catchError((error) => {
                    console.error('Error fetching next period projection:', error);
                    return throwError(error);
                }),
            );
    }

    /**
     * Obtener balance de vacaciones de un usuario específico
     */
    getUserVacationBalance(userId: number): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerGetBalanceByUserId(userId)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error fetching vacation balance for user ${userId}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Recalcular balance de vacaciones
     */
    recalculateBalance(userId: number): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerCalculateBalance(userId)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error recalculating balance for user ${userId}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Recalcula y guarda la política del balance usando los datos existentes.
     * Útil para corregir el workerType cuando el processNumber del usuario cambió.
     */
    recalculateBalancePolicy(userId: number, initialCutoffDays: number, cutoffDate: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerCreateInitialBalance({
                userId,
                initialCutoffDays,
                cutoffDate,
            })
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error recalculating policy for user ${userId}:`,
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Obtener la política de vacaciones del usuario actual
     */
    getMyPolicy(): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerGetUserSpecificPolicies()
            .pipe(
                catchError((error) => {
                    console.error('Error fetching my vacation policy:', error);
                    return throwError(error);
                }),
            );
    }

    // ==========================================
    // UTILIDADES
    // ==========================================

    /**
     * Calcular días hábiles entre dos fechas
     */
    calculateWorkingDays(startDate: Date, endDate: Date): number {
        let count = 0;
        const current = new Date(startDate);
        const end = new Date(endDate);

        while (current <= end) {
            const dayOfWeek = current.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                // No es domingo (0) ni sábado (6)
                count++;
            }
            current.setDate(current.getDate() + 1);
        }

        return count;
    }

    /**
     * Obtener días festivos (asuetos) de una empresa
     */
    getHolidaysByCompany(companyId: string): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerGetHolidaysByCompany(companyId)
            .pipe(
                catchError((error) => {
                    console.error(
                        `Error fetching holidays for company ${companyId}:`,
                        error,
                    );
                    return of({ data: [] });
                }),
            );
    }

    /**
     * Formatear estado de solicitud para mostrar
     */
    getStatusLabel(status: string): string {
        const statusLabels: { [key: string]: string } = {
            DRAFT: 'Borrador',
            PENDING: 'Pendiente',
            APPROVED: 'Aprobada',
            REJECTED: 'Rechazada',
            CANCELLED: 'Cancelada',
        };
        return statusLabels[status] || status;
    }

    /**
     * Obtener severity de PrimeNG según el estado
     */
    getStatusSeverity(status: string): string {
        const severityMap: { [key: string]: string } = {
            DRAFT: 'info',
            PENDING: 'warning',
            APPROVED: 'success',
            REJECTED: 'danger',
            CANCELLED: 'secondary',
        };
        return severityMap[status] || 'info';
    }

    // ==========================================
    // REPORTE DE SOLICITUDES
    // ==========================================

    /**
     * Obtener reporte de solicitudes de vacaciones con filtros
     * Usa el endpoint /vacations-requests-report/my-vacations
     * @param scope alcance del listado: 'mine' (propias y donde soy aprobador, por
     * defecto), 'pending' (pendientes de mi autorizacion) o 'all' (todas las que
     * permite mi rol)
     */
    getVacationRequestsReport(
        filter?: ReportFilter,
        includeDeleted?: boolean,
        search?: string,
        scope: VacationRequestScope = 'mine',
    ): Observable<any> {
        const filterString: VacationRequestsFilter | any = filter
            ? JSON.stringify(filter)
            : undefined;

        return this.vacationRequestsReportControllerService
            .vacationRequestsReportControllerGetMyVacationRequests(
                includeDeleted,
                filterString,
                search,
                scope,
            )
            .pipe(
                catchError((error) => {
                    console.error(
                        'Error fetching vacation requests report:',
                        error,
                    );
                    return throwError(error);
                }),
            );
    }

    /**
     * Obtener catálogos para filtros del reporte
     */
    getReportCatalogs(): Observable<any> {
        return this.vacationRequestsReportControllerService
            .vacationRequestsReportControllerGetCatalogs()
            .pipe(
                catchError((error) => {
                    console.error('Error fetching report catalogs:', error);
                    return throwError(error);
                }),
            );
    }

    /**
     * Obtener lista de balances de vacaciones con paginación
     * @param companyIds - IDs de empresas separados por coma (opcional)
     * @param search - Texto de búsqueda (opcional)
     * @param limit - Límite de registros (por defecto 10)
     * @param offset - Offset para paginación (por defecto 0)
     */
    getVacationBalances(
        companyIds?: string,
        search?: string,
        limit: number = 10,
        offset: number = 0,
    ): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerGetBalances(
                companyIds,
                search,
                limit,
                offset,
            )
            .pipe(
                catchError((error) => {
                    console.error('Error fetching vacation balances:', error);
                    return throwError(error);
                }),
            );
    }

    setManualPendingDays(userId: number, daysPending: number): Observable<any> {
        const body: VacationRequestsControllerSetManualPendingDaysRequest = { daysPending };
        return this.vacationRequestControllerService
            .vacationRequestsControllerSetManualPendingDays(userId, body)
            .pipe(catchError((error) => throwError(error)));
    }

    setPolicyOverride(userId: number, policyTypeOverride: string | null): Observable<any> {
        return this.vacationRequestControllerService
            .vacationRequestsControllerSetPolicyOverride(userId, { policyTypeOverride } as any)
            .pipe(catchError((error) => throwError(error)));
    }

    /**
     * Exporta el template de balances
     */
    getVacationTemplateBalancesExcel(): Observable<Blob> {
        return this.vacationRequestControllerService.vacationRequestsControllerDownloadUploadTemplate();
    }

    downloadVacationRequestPdf(id: string): Observable<Blob> {
        return this.vacationRequestControllerService.vacationRequestsControllerGeneratePdf(id);
    }

    /**
     * Importa los balances desde un archivo Excel
     */
    importVacationBalancesFromExcel(formData: FormData): Observable<any> {
        // Extraer el archivo del FormData
        const file = formData.get('files') as File;

        if (!file) {
            throw new Error('No se encontró archivo en el FormData');
        }

        // Convertir File a Blob y pasarlo como array
        const filesArray: Blob[] = [file as Blob];

        return this.vacationRequestControllerService.vacationRequestsControllerUploadInitialBalances(
            filesArray,
        );
    }

    /**
     * Exporta el reporte de balances de vacaciones a Excel
     */
    exportVacationRequestExcel(
        filter?: ReportFilter,
        includeDeleted = false,
        search?: string,
        scope: VacationRequestScope = 'mine',
    ): Observable<Blob> {
        const filterParam: { [key: string]: any } = {};

        if (filter) {
            if (filter.where) {
                filterParam['where'] = filter.where;
            }

            if (filter.order && filter.order.length > 0) {
                filterParam['order'] = filter.order;
            }
        }

        // Convertir el filtro a JSON string
        const filterString: VacationRequestsFilter | any =
            Object.keys(filterParam).length > 0
                ? JSON.stringify(filterParam)
                : undefined;

        return this.vacationRequestsReportControllerService.vacationRequestsReportControllerExportToExcel(
            includeDeleted,
            filterString,
            search,
            scope,
        );
    }
}
